import { ref } from 'vue'
import { AgentActionsService } from '@/types/generated'

/**
 * Composable for handling agent action ratings (thumbs up/down).
 *
 * Provides a reusable way to rate AI agent actions with positive or negative feedback.
 * Supports negative feedback with variant selection and optional comments.
 *
 * :return: Rating state and functions for handling ratings
 */
export function useAgentActionRating() {
  // Rating state
  const actionRatings = ref<Record<string, 'positive' | 'negative'>>({})
  const isSubmittingRating = ref(false)

  // Negative rating modal state
  const ratingModalActionUuid = ref<string | null>(null)
  const ratingNegativeVariant = ref<string>('')
  const ratingNegativeComment = ref<string>('')

  /**
   * Initialize ratings from fetched action data.
   *
   * :param actions: Array of actions with rating data
   */
  const initializeRatingsFromActions = (
    actions: Array<{ uuid: string; rating?: string | number | null }>
  ) => {
    for (const action of actions) {
      if (action.rating !== null && action.rating !== undefined) {
        const ratingValue = parseFloat(String(action.rating))
        actionRatings.value[action.uuid] = ratingValue >= 2.5 ? 'positive' : 'negative'
      }
    }
  }

  /**
   * Submit a positive rating for an action.
   *
   * :param actionUuid: UUID of the action to rate
   */
  const handlePositiveRating = async (actionUuid: string) => {
    if (actionRatings.value[actionUuid]) return

    isSubmittingRating.value = true
    try {
      await AgentActionsService.agentActionsRateCreate({
        requestBody: {
          agent_action_uuid: actionUuid,
          rating: '5.0'
        }
      })
      actionRatings.value[actionUuid] = 'positive'
    } catch (error) {
      console.error('Error submitting positive rating:', error)
    } finally {
      isSubmittingRating.value = false
    }
  }

  /**
   * Open the negative rating modal for an action.
   *
   * :param actionUuid: UUID of the action to rate negatively
   */
  const openNegativeRatingModal = (actionUuid: string) => {
    if (actionRatings.value[actionUuid]) return
    ratingModalActionUuid.value = actionUuid
    ratingNegativeVariant.value = ''
    ratingNegativeComment.value = ''
  }

  /**
   * Close the negative rating modal and reset state.
   */
  const closeNegativeRatingModal = () => {
    ratingModalActionUuid.value = null
    ratingNegativeVariant.value = ''
    ratingNegativeComment.value = ''
  }

  /**
   * Submit a negative rating with variant and optional comment.
   */
  const submitNegativeRating = async () => {
    if (!ratingModalActionUuid.value || !ratingNegativeVariant.value) return

    isSubmittingRating.value = true
    try {
      await AgentActionsService.agentActionsRateCreate({
        requestBody: {
          agent_action_uuid: ratingModalActionUuid.value,
          rating: '0.0',
          rating_negative_variant: ratingNegativeVariant.value as any,
          rating_negative_comment: ratingNegativeComment.value || undefined
        }
      })
      actionRatings.value[ratingModalActionUuid.value] = 'negative'
      closeNegativeRatingModal()
    } catch (error) {
      console.error('Error submitting negative rating:', error)
    } finally {
      isSubmittingRating.value = false
    }
  }

  /**
   * Check if an action has been rated.
   *
   * :param actionUuid: UUID of the action to check
   * :return: The rating type ('positive' | 'negative') or undefined
   */
  const getActionRating = (actionUuid: string) => {
    return actionRatings.value[actionUuid]
  }

  /**
   * Check if an action can be rated (not already rated).
   *
   * :param actionUuid: UUID of the action to check
   * :return: True if the action can be rated
   */
  const canRate = (actionUuid: string) => {
    return !actionRatings.value[actionUuid] && !isSubmittingRating.value
  }

  return {
    // State
    actionRatings,
    isSubmittingRating,
    ratingModalActionUuid,
    ratingNegativeVariant,
    ratingNegativeComment,

    // Functions
    initializeRatingsFromActions,
    handlePositiveRating,
    openNegativeRatingModal,
    closeNegativeRatingModal,
    submitNegativeRating,
    getActionRating,
    canRate
  }
}
